import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    inject,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { Observable, Subject, filter, map, tap } from 'rxjs';
import { nameOf } from '../../../../core/functions/string-helpers';
import { FilterOption } from '../../../../core/models/grid/filter-option';
import { IGridComponent } from '../../../../core/models/grid/grid-component';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { CurrencyFormattingService } from '../../../../core/services/currency-formatting/currency-formatting.service';
import { GridFilterDataService } from '../../../../core/services/grid-filter-data/grid-filter-data.service';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { ApplicationIdCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/dashboard/application-id-cell-renderer/application-id-cell-renderer.component';
import { OrderIdCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/order-fulfillment/order-id-cell-renderer/order-id-cell-renderer.component';
import { OrderStatusCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/order-fulfillment/order-status-cell-renderer/order-status-cell-renderer.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { MultiSelectFilterComponent } from '../../../../shared/components/grid/multi-select-filter/multi-select-filter.component';
import {
    OrderStatus,
    OrderStatusLabels,
} from '../shared/constants/order-status';
import { OrderService } from '../shared/services/order/order.service';
import { OrderStaffGridData } from './models/order-staff-grid-data';
import { OrderStaffGridResponse } from './models/order-staff-grid-response';
import { formatToDate } from '../../../../core/functions/date-helpers';
import { ApplicationGridService } from '../../../shared/services/application-grid.service';
import { RouteConstants } from '../../../../core/constants/routes';

@Component({
    selector: 'app-order-fulfillment',
    templateUrl: './order-fulfillment.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular, GridToolbarComponent],
})
export class OrderFulfillmentComponent implements IGridComponent, OnDestroy {
    private destroy$ = new Subject<void>();

    private readonly orderService = inject(OrderService);
    private readonly currencyFormattingService = inject(
        CurrencyFormattingService,
    );
    private readonly filterDataService = inject(GridFilterDataService);
    private readonly resourcesService = inject(ResourcesService);

    constructor() {}

    orderStatusFilterOptions: FilterOption[] =
        this.filterDataService.getOrderStatusFilterOptions();
    permittedOrderStatusTransitions: Partial<
        Record<keyof typeof OrderStatus, OrderStatus[]>
    > = {};

    context: unknown;
    rowData: OrderStaffGridData[] = [];
    gridApi: GridApi | undefined;

    columnDefs: ColDef[] = [];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.loadDropdownData()
            .pipe(
                tap(() => {
                    this.loadGridData();
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadDropdownData(): Observable<void> {
        return this.resourcesService.getPermittedOrderStatusTransitions().pipe(
            tap((permittedOrderStatusTransitions) => {
                this.permittedOrderStatusTransitions =
                    permittedOrderStatusTransitions;
            }),
            map(() => void 0),
        );
    }

    private loadGridData() {
        this.orderService
            .getAll()
            .pipe(
                tap((orders) => {
                    this.rowData = this.transformRowData(orders);
                    this.columnDefs = this.getColumnDefs();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private transformRowData(
        orders: OrderStaffGridResponse[],
    ): OrderStaffGridData[] {
        return orders.map((order) => ({
            ...order,
            customerName: [order.customerFirstName, order.customerLastName]
                .filter(Boolean)
                .join(' '),
            totalPrice: this.currencyFormattingService.formatToUSD(
                order.totalPrice,
            ),
            balance: this.currencyFormattingService.formatToUSD(order.balance),
            createdAt: formatToDate(order.createdAt),
            updatedAt: formatToDate(order.updatedAt),
        }));
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                field: nameOf<OrderStaffGridData>('orderId'),
                headerName: 'Order ID',
                cellRenderer: OrderIdCellRendererComponent,
                minWidth: 120,
                maxWidth: 150,
            },
            {
                field: nameOf<OrderStaffGridData>('salmonLogId'),
                headerName: 'App ID',
                cellRenderer: ApplicationIdCellRendererComponent,
                cellRendererParams: {
                    returnRoute: RouteConstants.Order.getFullRoute('INDEX'),
                },
                valueGetter: (params) => {
                    return ApplicationGridService.formatApplicationIdColumn(
                        params.data,
                    );
                },
                minWidth: 150,
                maxWidth: 180,
            },
            {
                field: nameOf<OrderStaffGridData>('createdAt'),
                headerName: 'Created Date',
                ...GridHelper.defaultDateTimeRendererColDef,
                minWidth: 170,
                maxWidth: 200,
            },
            {
                field: nameOf<OrderStaffGridData>('updatedAt'),
                headerName: 'Updated Date',
                ...GridHelper.defaultDateTimeRendererColDef,
                sort: 'desc',
                minWidth: 170,
                maxWidth: 200,
            },
            {
                field: nameOf<OrderStaffGridData>('customerName'),
                headerName: 'User',
                minWidth: 150,
                flex: 1,
            },
            {
                field: nameOf<OrderStaffGridData>('totalPrice'),
                headerName: 'Total',
                filter: false,
                minWidth: 110,
                maxWidth: 140,
            },
            {
                field: nameOf<OrderStaffGridData>('balance'),
                headerName: 'Balance',
                filter: false,
                minWidth: 110,
                maxWidth: 140,
            },
            {
                field: nameOf<OrderStaffGridData>('advisorName'),
                headerName: 'Advisor',
                minWidth: 160,
                flex: 1,
            },
            {
                field: nameOf<OrderStaffGridData>('status'),
                headerName: 'Order Status',
                cellRenderer: OrderStatusCellRendererComponent,
                cellRendererParams: () => ({
                    permittedOrderStatusTransitions:
                        this.permittedOrderStatusTransitions,
                }),
                valueGetter: (params) => {
                    const rowData = params.data as OrderStaffGridData;
                    return OrderStatusLabels[rowData.status];
                },
                filter: MultiSelectFilterComponent,
                filterParams: {
                    filterOptions: this.orderStatusFilterOptions.map(
                        (option) => ({
                            label: option.label,
                            value: option.value,
                            selected: option.selected,
                        }),
                    ),
                },
                ...GridHelper.disableCellFocusBorderColDef,
                minWidth: 220,
                maxWidth: 260,
            },
        ];
    }
}
